import React, { useEffect, useState } from "react";
import { apiUrl } from "../config/api";

const formatDate = (date) => {
  if (!date) return "Not specified";

  return new Date(date).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const stripHtml = (content) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content || "", "text/html");

  return doc.body.textContent || "";
};

const getImageUrl = (image) => {
  if (!image) return "";
  if (/^https?:\/\//i.test(image)) return image;

  return apiUrl(image);
};

const normalizeNotice = (notice) => {
  const plainContent = stripHtml(notice.content);
  const image = notice.image_url || notice.image || "";

  return {
    id: notice.id,
    title: notice.title || "Untitled public notice",
    dateIssued: formatDate(notice.published_date || notice.created_at),
    image: getImageUrl(image),
    summary:
      plainContent.length > 180
        ? `${plainContent.slice(0, 180).trim()}...`
        : plainContent,
    details: notice.content || "",
  };
};

const PublicNotices = () => {
  const [notices, setNotices] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchNotices = async () => {
      try {
        const res = await fetch(apiUrl("/api/public-notices"));
        if (!res.ok) throw new Error("Failed to fetch public notices");

        const data = await res.json();

        if (mounted) {
          setNotices(Array.isArray(data) ? data.map(normalizeNotice) : []);
          setError("");
        }
      } catch (err) {
        if (mounted) {
          setNotices([]);
          setError("Unable to load public notices at the moment.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchNotices();

    return () => {
      mounted = false;
    };
  }, []);

  const filteredNotices = notices.filter(
    (notice) =>
      notice.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      notice.summary.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* HEADER */}
      <section className="about-header">
        <div className="overlay">
          <h1 className="page-title">Public Notices</h1>
        </div>
      </section>

      <div className="public-notices-page">
        {/* SEARCH */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search public notices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* CARDS */}
        <div className="public-notices-container">
          {loading ? (
            <p className="no-results">Loading public notices...</p>
          ) : error ? (
            <p className="no-results">{error}</p>
          ) : filteredNotices.length > 0 ? (
            filteredNotices.map((notice) => (
              <div
                key={notice.id}
                className="notice-card"
                onClick={() => setSelectedNotice(notice)}
              >
                {notice.image && (
                  <img
                    src={notice.image}
                    alt={notice.title}
                    className="notice-image"
                  />
                )}

                <div className="notice-content">
                  <h3 className="notice-title">{notice.title}</h3>

                  <p className="notice-date">
                    <strong>Date Issued:</strong> {notice.dateIssued}
                  </p>

                  <p className="notice-summary">{notice.summary}</p>
                </div>

                <button className="view-details-btn">
                  View Details
                </button>
              </div>
            ))
          ) : (
            <p className="no-results">No public notices found.</p>
          )}
        </div>

        {/* MODAL */}
        {selectedNotice && (
          <div
            className="modal-overlay"
            onClick={() => setSelectedNotice(null)}
          >
            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="close-btn"
                onClick={() => setSelectedNotice(null)}
              >
                X
              </button>

              {selectedNotice.image && (
                <img
                  src={selectedNotice.image}
                  alt=""
                  className="modal-image"
                />
              )}

              <h2>{selectedNotice.title}</h2>

              <p className="modal-date">
                <strong>Date Issued:</strong> {selectedNotice.dateIssued}
              </p>

              <p className="modal-summary">{selectedNotice.summary}</p>

              <div
                className="modal-details"
                dangerouslySetInnerHTML={{
                  __html: selectedNotice.details,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* CSS */}
      <style>{`
        .public-notices-page {
          padding: 20px;
        }

        .search-bar input {
          width: 100%;
          padding: 10px;
          margin-bottom: 20px;
          border-radius: 6px;
          border: 1px solid #ccc;
        }

        .public-notices-container {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 20px;
        }

        .notice-card {
          background: #fff;
          border-radius: 10px;
          overflow: hidden;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .notice-image {
          width: 100%;
          max-height: 400px;
          object-fit: contain;
          background: #f5f5f5;
        }

        .notice-content {
          padding: 12px;
        }

        .notice-title {
          font-size: 16px;
          margin-bottom: 6px;
        }

        .notice-date {
          font-size: 12px;
          color: gray;
          margin-bottom: 6px;
        }

        .notice-summary {
          font-size: 14px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .view-details-btn {
          width: 100%;
          padding: 8px;
          border: none;
          background: #0a66c2;
          color: white;
          cursor: pointer;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.85);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
        }

        .modal-content {
          background: white;
          width: 70%;
          max-height: 90vh;
          overflow-y: auto;
          border-radius: 12px;
          padding: 25px;
          position: relative;
        }

        .close-btn {
          position: absolute;
          top: 10px;
          right: 10px;
          background: red;
          color: white;
          border: none;
          width: 35px;
          height: 35px;
          border-radius: 50%;
          cursor: pointer;
          font-weight: bold;
        }

        .modal-image {
          width: 100%;
          max-height: 450px;
          object-fit: contain;
          margin-bottom: 15px;
          background: #f5f5f5;
        }

        .modal-details {
          margin-top: 15px;
          line-height: 1.8;
          text-align: justify;
        }
      `}</style>
    </>
  );
};

export default PublicNotices;
